"use client";
import React, { useState } from "react";
import { X } from "lucide-react";

interface DealBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  preceptorName?: string;
  preceptorSpecialty?: string;
  preceptorCity?: string;
  onSubmit?: (formData: DealBookingFormData) => void;
}

export interface DealBookingFormData {
  studentName: string;
  specialty: string;
  preferredLocation: string;
  message: string;
}

export const DealBookingModal: React.FC<DealBookingModalProps> = ({
  isOpen,
  onClose,
  preceptorName,
  preceptorSpecialty,
  preceptorCity,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<DealBookingFormData>({
    studentName: "",
    specialty: preceptorSpecialty || "",
    preferredLocation: preceptorCity || "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.studentName.trim()) {
      alert("Please enter your name");
      return;
    }
    
    if (!formData.message.trim()) {
      alert("Please enter a message");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Call the onSubmit callback if provided
      if (onSubmit) {
        await onSubmit(formData);
      }
      
      // Send message to backend API
      const messagePayload = {
        preceptor_name: preceptorName,
        specialty: preceptorSpecialty,
        city: preceptorCity,
        student_name: formData.studentName,
        preferred_location: formData.preferredLocation,
        message: formData.message,
        created_at: new Date().toISOString(),
      };
      
      try {
        const response = await fetch('/api/preceptor-messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(messagePayload),
        });
        if (!response.ok) {
          console.warn('Failed to save message to backend');
        }
      } catch (apiError) {
        console.warn('Error saving message to backend:', apiError);
      }
      
      console.log("📧 Message processed:", formData);
      
      // Reset form and close modal
      setFormData({
        studentName: "",
        specialty: preceptorSpecialty || "",
        preferredLocation: preceptorCity || "",
        message: "",
      });
      onClose();
      
      alert("Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
        style={{
          animation: "fadeIn 0.2s ease-out",
        }}
      />

      {/* Modal */}
      <div
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl z-50 w-full max-w-md"
        style={{
          animation: "slideUp 0.3s ease-out",
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">
            Send Deal Booking Message
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Preceptor Info (Display Only) */}
          {preceptorName && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-gray-900">
                  Preceptor:
                </span>{" "}
                {preceptorName}
              </p>
            </div>
          )}

          {/* Student Name */}
          <div>
            <label htmlFor="studentName" className="block text-sm font-semibold text-gray-700 mb-1">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="studentName"
              name="studentName"
              value={formData.studentName}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Specialty */}
          <div>
            <label htmlFor="specialty" className="block text-sm font-semibold text-gray-700 mb-1">
              Specialty <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="specialty"
              name="specialty"
              value={formData.specialty}
              onChange={handleInputChange}
              placeholder="e.g., Cardiology, Emergency Medicine"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Preferred Location */}
          <div>
            <label htmlFor="preferredLocation" className="block text-sm font-semibold text-gray-700 mb-1">
              Preferred Location
            </label>
            <input
              type="text"
              id="preferredLocation"
              name="preferredLocation"
              value={formData.preferredLocation}
              onChange={handleInputChange}
              placeholder="e.g., Los Angeles, CA"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Describe your deal booking requirements and preferred dates..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-md hover:from-blue-700 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translate(-50%, -45%);
            opacity: 0;
          }
          to {
            transform: translate(-50%, -50%);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};
